import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Separator,
  Spinner,
  Table,
} from '@chakra-ui/react';
import {
  HiCheckCircle,
  HiExclamationTriangle,
  HiXCircle,
} from 'react-icons/hi2';
import { StatusBadge, Button as ButtonComponent } from '@themes/aegix';
import { useConcretePlanReadiness, useReunificationSite } from '../../hooks/queries/useReunification';
import { useUsers } from '../../hooks/queries/useUsers';
import {
  getSiteData,
  STAFF_ROLE_OPTIONS,
  type PlanApplicableSiteWithPlan,
  type PlanFloorplan,
  type ReadinessBreakdown,
  type ReadinessCheckItem,
  type StationMapping,
  type ZoneMapping,
  type ConcreteStaffAssignment,
} from '../../types/ReunificationTypes';

// =============================================================================
// Helpers
// =============================================================================

const fmtPct = (ratio: number) => (Number(ratio) * 100).toFixed(1);

const humanizeStationType = (slug: string) =>
  slug
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');

function formatStationLabel(
  mapping: StationMapping,
  stationNames: Map<string, string>,
): string {
  const resolved = stationNames.get(mapping.plan_station_public_id);
  if (resolved) return resolved;
  if (mapping.auto_generated && mapping.station_type) {
    return `Auto: ${humanizeStationType(mapping.station_type)}`;
  }
  return 'Unknown station';
}

function formatZoneLabel(
  mapping: ZoneMapping,
  zoneNames: Map<string, string>,
): string {
  return zoneNames.get(mapping.plan_zone_public_id) ?? 'Unknown zone';
}

function formatRoomLabel(
  roomId: string | undefined | null,
  featureNames: Map<string, string>,
): string {
  if (!roomId) return 'Unassigned';
  return featureNames.get(roomId) ?? 'Unknown room';
}

// =============================================================================
// Sub-components
// =============================================================================

function ReadinessSummary({
  site,
  breakdown,
  isLoadingBreakdown,
}: {
  site: PlanApplicableSiteWithPlan;
  breakdown: ReadinessBreakdown | undefined;
  isLoadingBreakdown: boolean;
}) {
  const score = site.readiness_score ?? 0;
  const isReady = site.is_ready ?? false;

  return (
    <Box>
      <Text fontSize="xs" fontWeight="600" color="text.secondary" mb={2}>
        Readiness Summary
      </Text>
      <HStack gap={4} mb={2}>
        <StatusBadge status={isReady ? 'active' : 'pending'} label={isReady ? 'Ready' : 'Not Ready'} />
        <HStack gap={2}>
          <Box
            w="120px"
            h="8px"
            bg="gray.200"
            borderRadius="full"
            overflow="hidden"
          >
            <Box
              h="100%"
              w={`${score}%`}
              bg={score >= 80 ? 'green.500' : score >= 50 ? 'yellow.500' : 'red.500'}
              borderRadius="full"
              transition="width 0.3s ease"
            />
          </Box>
          <Text fontSize="xs" fontWeight="500" color="text.secondary">
            {fmtPct(score / 100)}%
          </Text>
        </HStack>
      </HStack>

      {isLoadingBreakdown && <Spinner size="xs" />}

      {breakdown?.breakdown && breakdown.breakdown.length > 0 && (
        <Box mt={2}>
          {breakdown.breakdown.map((category) => (
            <Box key={category.category} mb={2}>
              <Text fontSize="xs" fontWeight="600" color="text.secondary" mb={1}>
                {category.category}
              </Text>
              <VStack gap={0} align="stretch">
                {category.items.map((item: ReadinessCheckItem) => (
                  <HStack key={item.name} gap={2} py={0.5}>
                    {item.status === 'pass' && (
                      <Box color="green.500" flexShrink={0}>
                        <HiCheckCircle size={14} />
                      </Box>
                    )}
                    {item.status === 'warn' && (
                      <Box color="yellow.500" flexShrink={0}>
                        <HiExclamationTriangle size={14} />
                      </Box>
                    )}
                    {item.status === 'fail' && (
                      <Box color="text.danger" flexShrink={0}>
                        <HiXCircle size={14} />
                      </Box>
                    )}
                    <Text fontSize="xs" color="text.primary">
                      {item.name}
                    </Text>
                    <Text fontSize="xs" color="text.secondary">
                      {item.detail}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function StationMappingsTable({
  mappings,
  stationNames,
  featureNames,
}: {
  mappings: StationMapping[];
  stationNames: Map<string, string>;
  featureNames: Map<string, string>;
}) {
  if (!mappings || mappings.length === 0) {
    return (
      <Box>
        <Text fontSize="sm" color="text.tertiary">
          Run Generate to create mappings
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        borderWidth="1px"
        borderColor="border.primary"
        borderRadius="lg"
        overflow="hidden"
        bg="surface.primary"
      >
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row bg="surface.secondary">
              <Table.ColumnHeader>
                <Text fontSize="xs" fontWeight="semibold" color="text.secondary" textTransform="uppercase">
                  Plan Station
                </Text>
              </Table.ColumnHeader>
              <Table.ColumnHeader>
                <Text fontSize="xs" fontWeight="semibold" color="text.secondary" textTransform="uppercase">
                  Site Feature
                </Text>
              </Table.ColumnHeader>
              <Table.ColumnHeader>
                <Text fontSize="xs" fontWeight="semibold" color="text.secondary" textTransform="uppercase">
                  Notes
                </Text>
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {mappings.map((mapping, idx) => (
              <Table.Row key={`${mapping.plan_station_public_id}-${idx}`} borderBottomWidth="1px" borderColor="border.primary">
                <Table.Cell py={2}>
                  <Text fontSize="sm" color="text.primary">
                    {formatStationLabel(mapping, stationNames)}
                  </Text>
                </Table.Cell>
                <Table.Cell py={2}>
                  <Text fontSize="sm" color="text.primary">
                    {formatRoomLabel(mapping.room_public_id, featureNames)}
                  </Text>
                </Table.Cell>
                <Table.Cell py={2}>
                  <Text fontSize="sm" color="text.secondary">
                    {mapping.notes || '--'}
                  </Text>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>
    </Box>
  );
}

function ZoneMappingsTable({
  mappings,
  zoneNames,
  featureNames,
}: {
  mappings: ZoneMapping[];
  zoneNames: Map<string, string>;
  featureNames: Map<string, string>;
}) {
  if (!mappings || mappings.length === 0) {
    return (
      <Box>
        <Text fontSize="sm" color="text.tertiary">
          Run Generate to create mappings
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        borderWidth="1px"
        borderColor="border.primary"
        borderRadius="lg"
        overflow="hidden"
        bg="surface.primary"
      >
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row bg="surface.secondary">
              <Table.ColumnHeader>
                <Text fontSize="xs" fontWeight="semibold" color="text.secondary" textTransform="uppercase">
                  Plan Zone
                </Text>
              </Table.ColumnHeader>
              <Table.ColumnHeader>
                <Text fontSize="xs" fontWeight="semibold" color="text.secondary" textTransform="uppercase">
                  Site Feature
                </Text>
              </Table.ColumnHeader>
              <Table.ColumnHeader>
                <Text fontSize="xs" fontWeight="semibold" color="text.secondary" textTransform="uppercase">
                  Notes
                </Text>
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {mappings.map((mapping, idx) => (
              <Table.Row key={`${mapping.plan_zone_public_id}-${idx}`} borderBottomWidth="1px" borderColor="border.primary">
                <Table.Cell py={2}>
                  <Text fontSize="sm" color="text.primary">
                    {formatZoneLabel(mapping, zoneNames)}
                  </Text>
                </Table.Cell>
                <Table.Cell py={2}>
                  <Text fontSize="sm" color="text.primary">
                    {formatRoomLabel(mapping.room_public_id, featureNames)}
                  </Text>
                </Table.Cell>
                <Table.Cell py={2}>
                  <Text fontSize="sm" color="text.secondary">
                    {mapping.notes || '--'}
                  </Text>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>
    </Box>
  );
}

const ROLE_LABEL_MAP = new Map(STAFF_ROLE_OPTIONS.map((o) => [o.value, o.label]));

function StaffAssignmentsTable({
  assignments,
  userNames,
  stationNames,
}: {
  assignments: ConcreteStaffAssignment[];
  userNames: Map<number, string>;
  stationNames: Map<string, string>;
}) {
  if (!assignments || assignments.length === 0) {
    return (
      <Box>
        <Text fontSize="sm" color="text.tertiary">
          No staff assignments configured
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        borderWidth="1px"
        borderColor="border.primary"
        borderRadius="lg"
        overflow="hidden"
        bg="surface.primary"
      >
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row bg="surface.secondary">
              <Table.ColumnHeader>
                <Text fontSize="xs" fontWeight="semibold" color="text.secondary" textTransform="uppercase">
                  User
                </Text>
              </Table.ColumnHeader>
              <Table.ColumnHeader>
                <Text fontSize="xs" fontWeight="semibold" color="text.secondary" textTransform="uppercase">
                  Role
                </Text>
              </Table.ColumnHeader>
              <Table.ColumnHeader>
                <Text fontSize="xs" fontWeight="semibold" color="text.secondary" textTransform="uppercase">
                  Station
                </Text>
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {assignments.map((assignment, index) => (
              <Table.Row key={`${assignment.user_id}-${index}`} borderBottomWidth="1px" borderColor="border.primary">
                <Table.Cell py={2}>
                  <Text fontSize="sm" color="text.primary">
                    {userNames.get(assignment.user_id) ?? `User #${assignment.user_id}`}
                  </Text>
                </Table.Cell>
                <Table.Cell py={2}>
                  <Text fontSize="sm" color="text.primary">
                    {ROLE_LABEL_MAP.get(assignment.role) ?? assignment.role}
                  </Text>
                </Table.Cell>
                <Table.Cell py={2}>
                  <Text fontSize="sm" color="text.secondary">
                    {assignment.station_public_id
                      ? (stationNames.get(assignment.station_public_id) ?? assignment.station_public_id)
                      : '--'}
                  </Text>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>
    </Box>
  );
}

// =============================================================================
// Main Component
// =============================================================================

interface ConcretePlanDetailProps {
  templateId: string;
  site: PlanApplicableSiteWithPlan;
  floorplans: PlanFloorplan[];
  onActivate: () => void;
  isActivating?: boolean;
  onGenerate?: () => void;
  isGenerating?: boolean;
  onEditStations?: () => void;
  onEditZones?: () => void;
  onEditStaff?: () => void;
}

export function ConcretePlanDetail({
  templateId,
  site,
  floorplans,
  onActivate,
  isActivating,
  onGenerate,
  isGenerating,
  onEditStations,
  onEditZones,
  onEditStaff,
}: ConcretePlanDetailProps) {
  const { t } = useTranslation();
  const isReady = site.is_ready ?? false;
  const { staff_assignments, station_mappings, zone_mappings } = getSiteData(site);

  const {
    data: breakdown,
    isLoading: isLoadingBreakdown,
  } = useConcretePlanReadiness(templateId, site.public_id);

  // Fetch site detail for feature names
  const { data: siteDetail } = useReunificationSite(site.site);

  // Fetch users for name resolution
  const { data: usersData } = useUsers({
    is_active: true,
    page_size: 100,
  });

  // Build lookup maps
  const stationNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const fp of floorplans) {
      for (const s of fp.stations) {
        map.set(s.public_id, s.name);
      }
    }
    return map;
  }, [floorplans]);

  const zoneNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const fp of floorplans) {
      for (const z of fp.zones) {
        map.set(z.public_id, z.name);
      }
    }
    return map;
  }, [floorplans]);

  const featureNames = useMemo(() => {
    const map = new Map<string, string>();
    // Build room name lookup from site detail
    if (siteDetail?.rooms) {
      for (const r of siteDetail.rooms) {
        map.set(r.public_id, r.name);
      }
    }
    return map;
  }, [siteDetail]);

  const userNameMap = useMemo(() => {
    const map = new Map<number, string>();
    if (usersData?.results) {
      for (const u of usersData.results) {
        const id = typeof u.id === 'string' ? parseInt(u.id, 10) : u.id;
        if (id && !isNaN(id)) {
          const name = [u.first_name, u.last_name].filter(Boolean).join(' ');
          map.set(id, name || u.email || `User #${id}`);
        }
      }
    }
    return map;
  }, [usersData]);

  return (
    <Box
      borderWidth="1px"
      borderColor="border.primary"
      borderRadius="lg"
      bg="surface.primary"
      p={4}
    >
      <VStack gap={4} align="stretch">
        {/* Header */}
        <HStack justify="space-between">
          <VStack gap={0} align="flex-start">
            <Text fontWeight="600" fontSize="sm" color="text.primary">
              {site.site_name}
            </Text>
            <Text fontSize="xs" color="text.secondary">
              {site.role_display} &middot; {t('reunification.priority', 'Priority')} {site.priority_order}
            </Text>
          </VStack>
          <HStack gap={2}>
            {onGenerate && (
              <ButtonComponent
                visual="secondary"
                size="sm"
                type="button"
                onClick={onGenerate}
                disabled={isGenerating}
                data-e2e="generate-button">
                {isGenerating ? t('reunification.generating', 'Generating...') : t('reunification.generate', 'Generate')}
              </ButtonComponent>
            )}
            <ButtonComponent
              visual="primary"
              size="sm"
              type="button"
              onClick={onActivate}
              disabled={!isReady || isActivating}
              data-e2e="activate-button">
              {isActivating ? t('reunification.activating', 'Activating...') : t('reunification.activate', 'Activate')}
            </ButtonComponent>
          </HStack>
        </HStack>

        <Separator />

        {/* Readiness */}
        <ReadinessSummary
          site={site}
          breakdown={breakdown}
          isLoadingBreakdown={isLoadingBreakdown}
        />

        <Separator />

        {/* Station Mappings */}
        <HStack justify="space-between" align="center">
          <HStack gap={2}>
            <Text fontSize="xs" fontWeight="600" color="text.secondary">
              {t('reunification.stationMappings', 'Station Mappings')}
            </Text>
            {breakdown && (
              <Text fontSize="xs" color="text.secondary">
                {fmtPct(breakdown.stations_pct)}%
              </Text>
            )}
          </HStack>
          {onEditStations && (
            <ButtonComponent visual="secondary" size="sm" type="button" onClick={onEditStations} data-e2e="edit-button">
              {t('common.edit', 'Edit')}
            </ButtonComponent>
          )}
        </HStack>
        <StationMappingsTable mappings={station_mappings} stationNames={stationNames} featureNames={featureNames} />

        <Separator />

        {/* Zone Mappings */}
        <HStack justify="space-between" align="center">
          <HStack gap={2}>
            <Text fontSize="xs" fontWeight="600" color="text.secondary">
              {t('reunification.zoneMappings', 'Zone Mappings')}
            </Text>
            {breakdown && (
              <Text fontSize="xs" color="text.secondary">
                {fmtPct(breakdown.zones_pct)}%
              </Text>
            )}
          </HStack>
          {onEditZones && (
            <ButtonComponent visual="secondary" size="sm" type="button" onClick={onEditZones} data-e2e="edit-button">
              {t('common.edit', 'Edit')}
            </ButtonComponent>
          )}
        </HStack>
        <ZoneMappingsTable mappings={zone_mappings} zoneNames={zoneNames} featureNames={featureNames} />

        <Separator />

        {/* Staff Assignments */}
        <HStack justify="space-between" align="center">
          <HStack gap={2}>
            <Text fontSize="xs" fontWeight="600" color="text.secondary">
              {t('reunification.staffAssignments', 'Staff Assignments')}
            </Text>
            {breakdown && (
              <Text fontSize="xs" color="text.secondary">
                {fmtPct(breakdown.roles_pct)}%
              </Text>
            )}
          </HStack>
          {onEditStaff && (
            <ButtonComponent visual="secondary" size="sm" type="button" onClick={onEditStaff} data-e2e="edit-button">
              {t('common.edit', 'Edit')}
            </ButtonComponent>
          )}
        </HStack>
        <StaffAssignmentsTable assignments={staff_assignments} userNames={userNameMap} stationNames={stationNames} />
      </VStack>
    </Box>
  );
}

export default ConcretePlanDetail;
